import { createHttpClient } from '@adapters/HttpClient';
import { CourierSettlementsApiAdapter, type CourierSettlementLinesPage } from '../../adapters';
import type { CourierSettlementLinesFilters } from '../../models/settlementLinesFilters';
import {
  EMPTY_SETTLEMENT_LINES_FILTERS,
  serializeSettlementLinesFiltersKey,
} from '../../models/settlementLinesFilters';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/** Tamaño de página para GET `/courier-settlements/:id/lines` */
export const COURIER_SETTLEMENT_LINES_PER_PAGE = 50;

export const useCourierSettlementDetail = (settlementId: number | undefined) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new CourierSettlementsApiAdapter(httpClient), [httpClient]);

  return useQuery<Record<string, unknown>, Error>({
    queryKey: ['courier-settlement-detail', settlementId],
    queryFn: () => adapter.detail(settlementId as number),
    enabled: typeof settlementId === 'number' && settlementId > 0,
    staleTime: 30 * 1000,
  });
};

export const useCourierSettlementLines = (
  settlementId: number | undefined,
  filters: CourierSettlementLinesFilters = EMPTY_SETTLEMENT_LINES_FILTERS,
) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new CourierSettlementsApiAdapter(httpClient), [httpClient]);

  const filtersKey = serializeSettlementLinesFiltersKey(filters);

  const query = useInfiniteQuery<CourierSettlementLinesPage, Error>({
    queryKey: ['courier-settlement-lines', settlementId, filtersKey],
    queryFn: ({ pageParam }) =>
      adapter.linesPaginated(settlementId as number, pageParam as number, COURIER_SETTLEMENT_LINES_PER_PAGE, filters),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    enabled: typeof settlementId === 'number' && settlementId > 0,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const lines = query.data?.pages.flatMap((p) => p.data) ?? [];
  const firstPage = query.data?.pages?.[0];
  const linesTotalFromApi =
    firstPage?.metaFromApi === true && firstPage.meta.total > 0 ? firstPage.meta.total : undefined;

  return {
    ...query,
    lines,
    linesTotalFromApi,
  };
};

export const useCourierSettlementExceptions = (settlementId: number | undefined) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new CourierSettlementsApiAdapter(httpClient), [httpClient]);

  return useQuery<unknown[], Error>({
    queryKey: ['courier-settlement-exceptions', settlementId],
    queryFn: () => adapter.exceptions(settlementId as number),
    enabled: typeof settlementId === 'number' && settlementId > 0,
    staleTime: 30 * 1000,
  });
};
